/**
 * Asset Repository Service
 * Manages centralized asset library for PMax campaigns
 */

import { db } from '../config/firebase';
import { doc, setDoc, getDoc, updateDoc, arrayUnion, Timestamp } from 'firebase/firestore';
import { StoredAsset } from './imageHarvester';

export interface AssetRepository {
  orgId: string;
  assets: {
    images: {
      square: StoredAsset[];      // 1:1 (min 300x300)
      landscape: StoredAsset[];   // 1.91:1 (min 600x314)
      portrait: StoredAsset[];    // 4:5 (min 480x600)
      logo: StoredAsset[];        // Square, transparent
    };
    videos: StoredAsset[];
    text: {
      headlines: string[];
      longHeadlines: string[];
      descriptions: string[];
    };
  };
  metadata: {
    createdAt: Date;
    lastUpdated: Date;
    totalAssets: number;
  };
}

export const assetRepository = {
  /**
   * Get or create asset repository for organization
   */
  async getRepository(orgId: string): Promise<AssetRepository> {
    try {
      const repoRef = doc(db, 'organizations', orgId, 'assetLibrary', 'repository');
      const repoDoc = await getDoc(repoRef);

      if (repoDoc.exists()) {
        const data = repoDoc.data();
        return {
          orgId,
          assets: data.assets || this.getEmptyAssets(),
          metadata: {
            createdAt: data.metadata?.createdAt?.toDate() || new Date(),
            lastUpdated: data.metadata?.lastUpdated?.toDate() || new Date(),
            totalAssets: data.metadata?.totalAssets || 0
          }
        };
      }

      // Create new repository
      const newRepo: AssetRepository = {
        orgId,
        assets: this.getEmptyAssets(),
        metadata: {
          createdAt: new Date(),
          lastUpdated: new Date(),
          totalAssets: 0
        }
      };

      await setDoc(repoRef, {
        assets: newRepo.assets,
        metadata: {
          createdAt: Timestamp.now(),
          lastUpdated: Timestamp.now(),
          totalAssets: 0
        }
      });

      return newRepo;
    } catch (error) {
      console.error('Error getting asset repository:', error);
      throw error;
    }
  },

  /**
   * Add harvested images to repository
   */
  async addHarvestedImages(
    orgId: string,
    images: StoredAsset[]
  ): Promise<void> {
    try {
      const repoRef = doc(db, 'organizations', orgId, 'assetLibrary', 'repository');

      // Categorize images
      const categorized = {
        square: images.filter(img => img.category === 'square'),
        landscape: images.filter(img => img.category === 'landscape'),
        portrait: images.filter(img => img.category === 'portrait'),
        logo: images.filter(img => img.category === 'logo')
      };

      // Update repository
      await updateDoc(repoRef, {
        'assets.images.square': arrayUnion(...categorized.square),
        'assets.images.landscape': arrayUnion(...categorized.landscape),
        'assets.images.portrait': arrayUnion(...categorized.portrait),
        'assets.images.logo': arrayUnion(...categorized.logo),
        'metadata.lastUpdated': Timestamp.now(),
        'metadata.totalAssets': images.length
      });

      console.log(`✅ Added ${images.length} images to repository`);
    } catch (error) {
      console.error('Error adding images to repository:', error);
      // If document doesn't exist, create it first
      await this.getRepository(orgId);
      // Retry
      await this.addHarvestedImages(orgId, images);
    }
  },

  /**
   * Get empty assets structure
   */
  getEmptyAssets(): AssetRepository['assets'] {
    return {
      images: {
        square: [],
        landscape: [],
        portrait: [],
        logo: []
      },
      videos: [],
      text: {
        headlines: [],
        longHeadlines: [],
        descriptions: []
      }
    };
  },

  /**
   * Get assets for campaign type
   */
  async getAssetsForCampaign(
    orgId: string,
    campaignType: 'search' | 'pmax'
  ): Promise<Partial<AssetRepository['assets']>> {
    const repo = await this.getRepository(orgId);

    if (campaignType === 'search') {
      return {
        text: repo.assets.text
      };
    }

    // PMax needs all assets
    return repo.assets;
  }
};

